import { useEffect, useRef } from "react";
import { useRouter } from "@tanstack/react-router";
import { getConsent, subscribeConsent } from "@/lib/analytics/consent";
import { isAdminPath } from "@/lib/analytics/route-utils";
import {
  capturePageview,
  ensureInitialized,
  isConfigPresent,
  reinstate,
  revokeAndReset,
} from "@/lib/analytics/posthog";

/**
 * Mounts once in the root. Emits at most one pageview per resolved route.
 * Never touches posthog-js on admin routes or before consent === 'granted'.
 */
export function PostHogProvider() {
  const router = useRouter();
  const lastCapturedKey = useRef<string | null>(null);

  useEffect(() => {
    if (!isConfigPresent()) return;

    const maybeCapture = () => {
      const path =
        typeof window !== "undefined" ? window.location.pathname : "";
      if (isAdminPath(path)) return;
      if (getConsent() !== "granted") return;
      if (lastCapturedKey.current === path) return;
      lastCapturedKey.current = path;
      void capturePageview(path, document?.title);
    };

    // Initial mount attempt.
    if (getConsent() === "granted" && !isAdminPath(window.location.pathname)) {
      void ensureInitialized().then(() => maybeCapture());
    }

    const unsubRouter = router.subscribe("onResolved", () => {
      // Route changed: reset dedupe if we moved off/on admin.
      maybeCapture();
    });

    const unsubConsent = subscribeConsent((next) => {
      if (next === "granted") {
        void reinstate().then(() => maybeCapture());
      } else if (next === "denied" || next === "unset") {
        revokeAndReset();
        lastCapturedKey.current = null;
      }
    });

    return () => {
      unsubRouter();
      unsubConsent();
    };
  }, [router]);

  return null;
}